import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../services/api';

const UploadPage = () => {
  const navigate = useNavigate();
  const [fileInfo, setFileInfo] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);
  const [storageLimit, setStorageLimit] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);

  useEffect(() => {
    // 检查是否是从其他页面拖入的文件
    const uploadFileFromDrag = sessionStorage.getItem('uploadFileFromDrag');
    const uploadFileData = sessionStorage.getItem('uploadFile');

    console.log('UploadPage 初始化:', { uploadFileFromDrag, uploadFileData });

    if (uploadFileFromDrag === 'true' && uploadFileData) {
      // 从其他页面拖入的文件，保留数据
      try {
        const file = JSON.parse(uploadFileData);
        console.log('解析到文件信息:', file);
        setFileInfo(file);
      } catch (error) {
        console.error('解析文件信息失败:', error);
        setFileInfo(null);
      }
      // 清除拖入标记，但不清除文件数据
      sessionStorage.removeItem('uploadFileFromDrag');
    } else {
      // 页面刷新或直接访问，清空所有数据
      console.log('页面刷新或直接访问，清空所有文件数据');
      sessionStorage.removeItem('uploadFile');
      sessionStorage.removeItem('uploadFileFromDrag');
      setFileInfo(null);
    }

    // 执行存储限制检测
    detectStorageLimit();
  }, []);

  // 音频播放控制
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !fileInfo) return;

    // 重置播放状态
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    // 强制重新加载音频以解决缓存问题
    audio.load();

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => {
      setDuration(audio.duration);
      console.log('音频时长更新:', audio.duration);
    };
    const handleEnded = () => setIsPlaying(false);
    const handleLoadStart = () => {
      console.log('开始加载音频');
      setCurrentTime(0);
      setDuration(0);
    };
    const handleCanPlay = () => {
      console.log('音频可以播放，时长:', audio.duration);
      setDuration(audio.duration);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadstart', handleLoadStart);
    audio.addEventListener('canplay', handleCanPlay);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadstart', handleLoadStart);
      audio.removeEventListener('canplay', handleCanPlay);
    };
  }, [fileInfo]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time) => {
    if (!time || !isFinite(time)) return '00:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // 动态检测sessionStorage限制 - 优化算法
  const detectSessionStorageLimit = async () => {
    setIsDetecting(true);
    return new Promise((resolve) => {
      const testKey = 'test_storage_limit_' + Date.now();

      // 阶段1：粗略检测（指数增长）
      let testSize = 1024; // 1KB开始
      let maxSize = 0;

      const coarseTest = () => {
        try {
          while (testSize <= 20 * 1024 * 1024) { // 测试到20MB
            const data = 'x'.repeat(testSize);
            sessionStorage.setItem(testKey, data);
            sessionStorage.removeItem(testKey);
            maxSize = testSize;
            testSize *= 2; // 翻倍测试
          }
        } catch (error) {
          // 粗略检测失败，进入精确检测阶段
          sessionStorage.removeItem(testKey);
          fineTest(maxSize / 2, maxSize); // 在失败大小和成功大小之间进行精确检测
        }
      };

      // 阶段2：精确检测（二分法）
      const fineTest = (minSize, maxSize) => {
        if (maxSize - minSize < 1024) { // 如果差距小于1KB，停止检测
          resolve(minSize);
          return;
        }

        const midSize = Math.floor((minSize + maxSize) / 2);
        try {
          const data = 'x'.repeat(midSize);
          sessionStorage.setItem(testKey, data);
          sessionStorage.removeItem(testKey);
          // 成功，继续测试更大的值
          fineTest(midSize, maxSize);
        } catch (error) {
          sessionStorage.removeItem(testKey);
          // 失败，测试更小的值
          fineTest(minSize, midSize);
        }
      };

      // 开始检测
      try {
        coarseTest();
      } catch (error) {
        sessionStorage.removeItem(testKey);
        resolve(5 * 1024 * 1024); // 如果连粗略检测都失败，返回默认值5MB
      }
    });
  };

  
  // 检测存储限制
  const detectStorageLimit = async () => {
    setIsDetecting(true);
    try {
      const limit = await detectSessionStorageLimit();
      setStorageLimit(limit);
      // 将检测结果存储到sessionStorage供全局使用
      sessionStorage.setItem('detectedStorageLimit', limit.toString());
      console.log('检测到的存储限制:', (limit / 1024 / 1024).toFixed(2) + 'MB');
    } catch (error) {
      console.error('存储限制检测失败:', error);
      const defaultLimit = 5 * 1024 * 1024; // 默认5MB
      setStorageLimit(defaultLimit);
      sessionStorage.setItem('detectedStorageLimit', defaultLimit.toString());
    } finally {
      setIsDetecting(false);
    }
  };

  // 文件拖拽处理 - 允许更新当前文件
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      // 验证文件类型
      const validTypes = ['audio/mp3', 'audio/mpeg', 'audio/wav', 'audio/x-wav', 'audio/mp4', 'audio/m4a', 'audio/ogg', 'audio/flac', 'audio/webm', 'video/webm'];

      if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|ogg|flac|webm)$/i)) {
        alert('不支持的文件类型，请选择音频文件');
        return;
      }

      
      // 停止当前播放
      if (audioRef.current) {
        audioRef.current.pause();
        setIsPlaying(false);
        setCurrentTime(0);
        setDuration(0);
      }

      // 更新文件信息
      const reader = new FileReader();
      reader.onload = (e) => {
        const newFileInfo = {
          name: file.name,
          size: file.size,
          type: file.type,
          dataUrl: e.target.result
        };

        setFileInfo(newFileInfo);
        sessionStorage.setItem('uploadFile', JSON.stringify(newFileInfo));
        // 在UploadPage内部拖入不需要设置拖入标记
      };

      reader.readAsDataURL(file);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleCancel = () => {
    // 清除 sessionStorage 中的文件信息
    sessionStorage.removeItem('uploadFile');
    navigate('/');
  };

  const handleGoBack = () => {
    navigate('/');
  };

  const handleStartTranscribe = async () => {
    if (!fileInfo) return;

    try {
      setIsProcessing(true);

      // 生成唯一的音频ID
      const audioId = apiService.generateAudioId();

      // 将 dataURL 转换为 Blob
      const response = await fetch(fileInfo.dataUrl);
      const audioBlob = await response.blob();

      // 估算音频时长（基于文件大小和类型）
      let estimatedDuration = 0;
      if (fileInfo.type.includes('webm')) {
        // WebM 格式估算
        const estimatedBitrate = 32 * 1024 / 8; // 32 kbps
        estimatedDuration = fileInfo.size / estimatedBitrate;
      } else {
        // 其他格式估算
        const estimatedBitrate = 128 * 1024 / 8; // 128 kbps
        estimatedDuration = fileInfo.size / estimatedBitrate;
      }

      // 1. 先在后端创建记录
      console.log('📝 创建音频记录...', { audioId, duration: estimatedDuration });
      const recordResponse = await apiService.createRecord(audioId, Math.round(estimatedDuration));

      if (recordResponse.success) {
        console.log('✅ 记录创建成功:', recordResponse.data.record);

        // 将音频数据转换为URL并存储到 sessionStorage
        const audioUrl = URL.createObjectURL(audioBlob);

        // 将audioBlob转换为base64字符串以便存储
        const base64Audio = await apiService.blobToBase64(audioBlob);
        const fullBase64 = `data:${audioBlob.type};base64,${base64Audio}`;

        sessionStorage.setItem('pendingAudio', JSON.stringify({
          audioId,
          duration: Math.round(estimatedDuration),
          size: audioBlob.size,
          audioUrl: audioUrl,
          audioBase64: fullBase64,
          mimeType: audioBlob.type
        }));

        // 清除上传文件信息
        sessionStorage.removeItem('uploadFile');

        // 2. 跳转到详情页面
        console.log('🔄 跳转到详情页面，audioId:', audioId);
        navigate(`/record/${audioId}`, { state: { from: 'upload' } });
      } else {
        throw new Error(recordResponse.error || '创建记录失败');
      }
    } catch (error) {
      console.error('处理上传文件时出错:', error);
      setIsProcessing(false);
      alert('处理文件失败，请重试');
    }
  };

  return (
    <div
      className="min-h-screen bg-black"
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="flex flex-col items-center justify-center text-white pb-20 min-h-screen px-8">
        {!fileInfo ? (
          // 未上传文件时的界面
          <div className="text-center">
            {/* 上传图标 */}
            <div className="mb-8">
              <svg width="120" height="120" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="mx-auto text-gray-400">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="17,8 12,3 7,8" />
                <line x1="12" y1="3" x2="12" y2="15" />
              </svg>
            </div>

            {/* 提示文字 */}
            <div className="text-3xl font-bold mb-4 text-white">请拖入音频文件</div>
            <div className="text-lg text-gray-400 mb-2">支持 MP3、WAV、M4A、OGG、FLAC、WebM 格式</div>

            {/* 存储限制检测结果 */}
            <div className="mb-6">
              {isDetecting ? (
                <div className="text-sm text-gray-500 flex items-center justify-center">
                  <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  正在检测存储限制...
                </div>
              ) : storageLimit ? (
                <div className="text-sm text-gray-500 text-center">
                  <div className="mb-1">浏览器存储限制检测完成</div>
                  <div>总存储空间: <span className="text-green-400 font-medium">{(storageLimit / 1024 / 1024).toFixed(2)}MB</span></div>
                  <div className="text-xs text-gray-600 mt-1">（仅供参考，不影响文件上传）</div>
                  <button
                    onClick={detectStorageLimit}
                    className="mt-2 text-xs text-gray-400 hover:text-gray-300 underline"
                  >
                    重新检测
                  </button>
                </div>
              ) : (
                <div className="text-sm text-gray-500">检测存储限制中...</div>
              )}
            </div>

            {/* 拖拽区域提示 */}
            <div className="border-2 border-dashed border-gray-600 rounded-lg p-12 max-w-md mb-8">
              <div className="text-gray-400">
                <div className="text-lg mb-2">将音频文件拖拽到此处</div>
                <div className="text-sm">或拖拽到页面任意位置</div>
              </div>
            </div>

            {/* 返回按钮 */}
            <button
              onClick={handleGoBack}
              className="px-8 py-3 bg-gray-700 rounded-full text-white font-medium hover:bg-gray-600 transition-colors"
            >
              返回首页
            </button>
          </div>
        ) : (
          // 已上传文件时的界面
          <div className="w-full max-w-md">
            {/* 标题 */}
            <div className="text-2xl font-bold mb-8 text-center">音频文件已上传</div>

            {/* 文件信息 */}
            <div className="bg-gray-900 rounded-lg p-6 mb-6">
              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-400 mb-1">文件名</div>
                  <div className="text-white break-all">{fileInfo.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">文件大小</div>
                  <div className="text-white">{formatFileSize(fileInfo.size)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">文件类型</div>
                  <div className="text-white">{fileInfo.type || '未知'}</div>
                </div>
              </div>
            </div>

            {/* 音频播放器 */}
            <div className="bg-gray-900 rounded-lg p-6 mb-6">
              <div className="space-y-4">
                {/* 播放控制 */}
                <div className="flex items-center space-x-4">
                  <button
                    onClick={togglePlayPause}
                    className="w-12 h-12 bg-primary-blue rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                  >
                    {isPlaying ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="6" y="4" width="4" height="16" />
                        <rect x="14" y="4" width="4" height="16" />
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    )}
                  </button>
                  <div className="flex-1 text-white text-sm">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </div>
                </div>

                {/* 进度条 */}
                <div
                  className="relative h-2 bg-gray-700 rounded-full cursor-pointer overflow-hidden"
                  onClick={handleSeek}
                >
                  <div
                    className="absolute h-full bg-primary-blue transition-all duration-100"
                    style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex flex-col space-y-4">
              <button
                onClick={handleStartTranscribe}
                disabled={isProcessing}
                className="w-full py-4 bg-primary-blue rounded-full text-white font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? '处理中...' : '开始转写'}
              </button>

              <button
                onClick={handleCancel}
                disabled={isProcessing}
                className="w-full py-4 bg-gray-700 rounded-full text-white font-medium hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                取消
              </button>
            </div>

            {/* 更新文件提示 */}
            <div className="text-center mt-6">
              <div className="text-sm text-gray-400">
                拖入新文件可替换当前文件
              </div>
            </div>
          </div>
        )}

        {/* 隐藏的音频元素 */}
        {fileInfo && (
          <audio
            ref={audioRef}
            src={`${fileInfo.dataUrl}#t=${Date.now()}`} // 添加时间戳避免缓存
            preload="metadata"
            key={`${fileInfo.name}-${fileInfo.size}-${Date.now()}`} // 使用文件信息+时间戳作为key
          />
        )}
      </div>
    </div>
  );
};

export default UploadPage;